import { nullSafeGetOrElse } from './DataAccessUtils';

// --- A project's template field values --------------------------------------
// A template's custom fields are rendered dynamically on the project forms, so
// their form names are built from the field key. A string name is a single segment
// in antd v4 (no dot splitting), so a key containing a period is still one field.
export const templateFieldName = (group, key) => `${group}-${key}-valueAsString`;

// { key, valueType, valueAsString } per template field, read back out of the form.
// Required fields always go; optional ones only when filled in. The key and type
// come from the template itself rather than from hidden mirror fields.
export const getProjectTemplateDetails = (projectTemplate, values) => {
	const detailsFor = (group) =>
		nullSafeGetOrElse(group, projectTemplate, []).map((field) => ({
			key: field.key,
			valueType: field.type,
			valueAsString: values[templateFieldName(group, field.key)],
		}));
	return detailsFor('requiredFields').concat(
		detailsFor('optionalFields').filter(
			(detail) => detail.valueAsString !== undefined && detail.valueAsString !== ''
		)
	);
};

// The saved values keyed by field key, for seeding the inputs once a template's
// fields exist. They mount only after a template is picked/fetched, so they can't
// ride along in the form's initialValues.
export const getTemplateDetailPrefill = (projectTemplate, projectTemplateDetails) => {
	const savedByKey = {};
	(projectTemplateDetails || []).forEach((detail) => {
		savedByKey[detail.key] = detail.valueAsString;
	});
	const prefill = {};
	['requiredFields', 'optionalFields'].forEach((group) => {
		nullSafeGetOrElse(group, projectTemplate, []).forEach((field) => {
			if (savedByKey[field.key] !== undefined) {
				prefill[templateFieldName(group, field.key)] = savedByKey[field.key];
			}
		});
	});
	return prefill;
};

// "Copy and create new" for a project template — same flow as an asset copy
// (getAssetCopy in assetUtils.ts): the source record becomes the form's initial
// values with its identity and audit trail stripped, so saving creates a new
// template rather than editing the source.
//
// Template items (deliverable templates with their nested tasks, and top-level
// task templates) are content on the template and carry no ids of their own, so
// they copy across wholesale — that's the point of the flow.
export const getProjectTemplateCopy = (projectTemplate) => ({
	...projectTemplate,
	id: undefined,
	name: `Copy of ${nullSafeGetOrElse('name', projectTemplate, '')}`,
	requiredFields: nullSafeGetOrElse('requiredFields', projectTemplate, []),
	optionalFields: nullSafeGetOrElse('optionalFields', projectTemplate, []),
	deliverableTemplates: nullSafeGetOrElse('deliverableTemplates', projectTemplate, []),
	taskTemplates: nullSafeGetOrElse('taskTemplates', projectTemplate, []),
	// Audit fields belong to the source record; the backend stamps its own on create.
	createdBy: undefined,
	createdByContact: undefined,
	createdAt: undefined,
	updatedBy: undefined,
	updatedByContact: undefined,
	updatedAt: undefined,
	isDeleted: undefined,
	deletedAt: undefined,
});
